/**
 * A frozen camera: the frames the scan analyses stop changing.
 *
 * On 2026-09-14 Perri's Android froze with her in cactus pose. For two minutes
 * the scan analysed one image — the same keypoint scores and body height, to
 * the decimal, across four attempts — so the countdown ran on a still picture
 * and the spin failed as "incomplete rotation" (36 of 42 ticks front-facing).
 * Nothing in the app noticed, and nothing in the logs said why it froze.
 *
 * A live camera never delivers the same frame twice 2 s apart: sensor noise
 * changes the pixels of every frame. Two CAPTURES of one frame can match — a
 * 15 fps camera read by the 15 Hz alignment loop — which is why this waits
 * 2 s rather than reacting to a single repeat.
 *
 * Pure, and imports nothing, like `liveHealth.ts`: `PoseDetectionManager`
 * writes it and both summary accumulators read it.
 */

/** Unchanged frames for this long mean the camera stopped. */
export const FROZEN_AFTER_MS = 2000;

/** Grid points sampled per axis: 24 x 24 = 576 pixels hashed per frame. */
const SAMPLES_PER_AXIS = 24;

/**
 * A sampled channel range this small means the frame has nothing to compare —
 * black (a covered lens, a dark room) or blown out. "The camera stopped" would
 * be the wrong advice there.
 */
const UNIFORM_RANGE = 2;

export interface FrameSignature {
  hash: number;
  uniform: boolean;
}

/** FNV-1a over a grid of pixels. Cheap enough for every analysed frame. */
export function frameSignature(image: {
  data: ArrayLike<number>;
  width: number;
  height: number;
}): FrameSignature {
  const data = image.data;
  const width = image.width;
  const height = image.height;
  const stepX = Math.max(1, Math.floor(width / SAMPLES_PER_AXIS));
  const stepY = Math.max(1, Math.floor(height / SAMPLES_PER_AXIS));

  let hash = 0x811c9dc5;
  let min = 255;
  let max = 0;
  for (let y = stepY >> 1; y < height; y += stepY) {
    for (let x = stepX >> 1; x < width; x += stepX) {
      const i = (y * width + x) * 4;
      for (let c = 0; c < 3; c++) {
        const v = data[i + c];
        hash ^= v;
        hash = Math.imul(hash, 0x01000193);
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
  }
  return { hash: hash >>> 0, uniform: max - min <= UNIFORM_RANGE };
}

export class VideoFreezeWatch {
  private lastHash: number | null = null;
  private runStart: number | null = null;
  private longestMs = 0;
  private frozen = false;
  private reported = false;

  observe(now: number, sig: FrameSignature): void {
    if (sig.uniform) {
      // No evidence either way, and a run must not span it.
      this.lastHash = null;
      this.runStart = null;
      return;
    }
    if (this.runStart === null || sig.hash !== this.lastHash) {
      this.lastHash = sig.hash;
      this.runStart = now;
      return;
    }
    const run = now - this.runStart;
    if (run > this.longestMs) this.longestMs = run;
    if (run >= FROZEN_AFTER_MS) this.frozen = true;
  }

  isFrozen(): boolean {
    return this.frozen;
  }

  /** True exactly once, the first time the watch sees a freeze. */
  takeNewFreeze(): boolean {
    if (!this.frozen || this.reported) return false;
    this.reported = true;
    return true;
  }

  /** The longest run of identical frames, for the summary rows. */
  frozenMs(): number {
    return Math.round(this.longestMs);
  }

  reset(): void {
    this.lastHash = null;
    this.runStart = null;
    this.longestMs = 0;
    this.frozen = false;
    this.reported = false;
  }
}

/** Frames analysed during alignment. Reset at each attempt. */
export const alignmentVideoFreeze = new VideoFreezeWatch();
/** Frames captured for the spin. Reset when a spin begins. */
export const rotationVideoFreeze = new VideoFreezeWatch();
